/**
 * Зашифрованное тело сообщения — UTF-8 строка.
 * Текст: JSON v1; старые сообщения — произвольная строка (legacy).
 */
export const CHAT_MESSAGE_PAYLOAD_V = 1 as const;

/** Ссылка на цитируемое сообщение внутри зашифрованного JSON (`mid` = id документа в Firestore). */
export type ChatPayloadReplyWire = {
  mid: string;
  pv: string;
};

export type ChatMessageReplyTo = {
  messageId: string;
  preview: string;
};

const REPLY_PREVIEW_WIRE_MAX = 240;

export function truncateReplyPreviewForWire(preview: string): string {
  const s = preview.replace(/\s+/g, ' ').trim();
  if (s.length <= REPLY_PREVIEW_WIRE_MAX) return s;
  return `${s.slice(0, REPLY_PREVIEW_WIRE_MAX)}…`;
}

function toWireReply(reply?: ChatMessageReplyTo | null): { r: ChatPayloadReplyWire } | Record<string, never> {
  if (!reply?.messageId) return {};
  return {
    r: {
      mid: reply.messageId,
      pv: truncateReplyPreviewForWire(reply.preview),
    },
  };
}

function parseReplyWire(r: unknown): ChatMessageReplyTo | undefined {
  if (!r || typeof r !== 'object' || Array.isArray(r)) return undefined;
  const o = r as Record<string, unknown>;
  if (typeof o.mid !== 'string' || o.mid.length === 0) return undefined;
  const pv = typeof o.pv === 'string' ? o.pv : '';
  return { messageId: o.mid, preview: pv };
}

export type ChatEncryptedTextPayload = {
  v: typeof CHAT_MESSAGE_PAYLOAD_V;
  t: 'text';
  b: string;
  r?: ChatPayloadReplyWire;
};

export type ChatEncryptedMediaPayload = {
  v: typeof CHAT_MESSAGE_PAYLOAD_V;
  t: 'media';
  /** image | video | file (например ZIP) */
  k: 'image' | 'video' | 'file';
  /** Полный путь объекта в Storage (корень бакета). */
  sp: string;
  mt: string;
  sz?: number;
  w?: number;
  h?: number;
  d?: number;
  r?: ChatPayloadReplyWire;
};

/** Текст + одно или несколько вложений в одном сообщении. */
export type ChatEncryptedTextMediaPayload = {
  v: typeof CHAT_MESSAGE_PAYLOAD_V;
  t: 'text_media';
  b: string;
  m: Array<{
    k: 'image' | 'video' | 'file';
    sp: string;
    mt: string;
    sz?: number;
    w?: number;
    h?: number;
    d?: number;
  }>;
  r?: ChatPayloadReplyWire;
};

export function serializeTextChatPayload(plainText: string, reply?: ChatMessageReplyTo | null): string {
  const p: ChatEncryptedTextPayload = {
    v: CHAT_MESSAGE_PAYLOAD_V,
    t: 'text',
    b: plainText,
    ...toWireReply(reply),
  };
  return JSON.stringify(p);
}

export function serializeMediaChatPayload(
  media: Omit<ChatEncryptedMediaPayload, 'v' | 't' | 'r'>,
  reply?: ChatMessageReplyTo | null
): string {
  const p: ChatEncryptedMediaPayload = {
    v: CHAT_MESSAGE_PAYLOAD_V,
    t: 'media',
    ...media,
    ...toWireReply(reply),
  };
  return JSON.stringify(p);
}

export type TextMediaItemForPayload = Pick<
  ChatEncryptedMediaPayload,
  'k' | 'sp' | 'mt' | 'sz' | 'w' | 'h' | 'd'
>;

export function serializeTextWithMediaChatPayload(
  caption: string,
  items: TextMediaItemForPayload[],
  reply?: ChatMessageReplyTo | null
): string {
  const m = items.map((x) => {
    const o: ChatEncryptedTextMediaPayload['m'][number] = {
      k: x.k,
      sp: x.sp,
      mt: x.mt,
    };
    if (typeof x.sz === 'number' && Number.isFinite(x.sz)) o.sz = x.sz;
    if (typeof x.w === 'number' && Number.isFinite(x.w)) o.w = x.w;
    if (typeof x.h === 'number' && Number.isFinite(x.h)) o.h = x.h;
    if (typeof x.d === 'number' && Number.isFinite(x.d)) o.d = x.d;
    return o;
  });
  const p: ChatEncryptedTextMediaPayload = {
    v: CHAT_MESSAGE_PAYLOAD_V,
    t: 'text_media',
    b: caption,
    m,
    ...toWireReply(reply),
  };
  return JSON.stringify(p);
}

export type ParsedChatPayload =
  | { kind: 'text'; text: string; replyTo?: ChatMessageReplyTo }
  | { kind: 'media'; attachment: ChatViewAttachment; replyTo?: ChatMessageReplyTo }
  | { kind: 'text_media'; text: string; attachments: ChatViewAttachment[]; replyTo?: ChatMessageReplyTo };

/** Префикс `storagePath` для исходящих вложений до записи в Firestore (не ходим в Storage). */
export const OPTIMISTIC_CHAT_ATTACHMENT_PREFIX = '__optimistic__/';

export function isOptimisticChatAttachmentStoragePath(p: string): boolean {
  return p.startsWith(OPTIMISTIC_CHAT_ATTACHMENT_PREFIX);
}

export type ChatViewAttachment = {
  kind: 'image' | 'video' | 'file';
  storagePath: string;
  mimeType: string;
  /** Имя для подписи и download; для файлов без превью — из пути Storage или локально при отправке. */
  fileName?: string;
  sizeBytes?: number;
  width?: number;
  height?: number;
  durationSec?: number;
  /** Локальный object URL превью при исходящей загрузке. */
  localPreviewUrl?: string;
  uploadStatus?: 'uploading' | 'failed';
  uploadProgressPercent?: number;
};

export function parseDecryptedChatPayload(decryptedUtf8: string): ParsedChatPayload {
  const s = decryptedUtf8.trimStart();
  if (!s.startsWith('{')) {
    return { kind: 'text', text: decryptedUtf8 };
  }
  try {
    const o = JSON.parse(decryptedUtf8) as unknown;
    if (!o || typeof o !== 'object' || Array.isArray(o)) {
      return { kind: 'text', text: decryptedUtf8 };
    }
    const r = o as Record<string, unknown>;
    if (r.v !== CHAT_MESSAGE_PAYLOAD_V) {
      return { kind: 'text', text: decryptedUtf8 };
    }
    const replyTo = parseReplyWire(r.r);

    if (r.t === 'text' && typeof r.b === 'string') {
      return replyTo ? { kind: 'text', text: r.b, replyTo } : { kind: 'text', text: r.b };
    }
    if (
      r.t === 'media' &&
      (r.k === 'image' || r.k === 'video' || r.k === 'file') &&
      typeof r.sp === 'string' &&
      r.sp.length > 0 &&
      typeof r.mt === 'string' &&
      r.mt.length > 0
    ) {
      const sp = r.sp;
      const lastSeg = sp.split('/').pop() ?? '';
      return {
        kind: 'media',
        attachment: {
          kind: r.k,
          storagePath: sp,
          mimeType: r.mt,
          ...(r.k === 'file' && lastSeg.length > 0 ? { fileName: lastSeg } : {}),
          sizeBytes: typeof r.sz === 'number' && Number.isFinite(r.sz) ? r.sz : undefined,
          width: typeof r.w === 'number' && Number.isFinite(r.w) ? r.w : undefined,
          height: typeof r.h === 'number' && Number.isFinite(r.h) ? r.h : undefined,
          durationSec: typeof r.d === 'number' && Number.isFinite(r.d) ? r.d : undefined,
        },
        ...(replyTo ? { replyTo } : {}),
      };
    }
    if (r.t === 'text_media' && typeof r.b === 'string' && Array.isArray(r.m) && r.m.length > 0) {
      const attachments: ChatViewAttachment[] = [];
      for (const raw of r.m) {
        if (!raw || typeof raw !== 'object' || Array.isArray(raw)) continue;
        const it = raw as Record<string, unknown>;
        if (it.k !== 'image' && it.k !== 'video' && it.k !== 'file') continue;
        if (typeof it.sp !== 'string' || it.sp.length === 0) continue;
        if (typeof it.mt !== 'string' || it.mt.length === 0) continue;
        const lastSeg = it.sp.split('/').pop() ?? '';
        attachments.push({
          kind: it.k,
          storagePath: it.sp,
          mimeType: it.mt,
          ...(it.k === 'file' && lastSeg.length > 0 ? { fileName: lastSeg } : {}),
          sizeBytes: typeof it.sz === 'number' && Number.isFinite(it.sz) ? it.sz : undefined,
          width: typeof it.w === 'number' && Number.isFinite(it.w) ? it.w : undefined,
          height: typeof it.h === 'number' && Number.isFinite(it.h) ? it.h : undefined,
          durationSec: typeof it.d === 'number' && Number.isFinite(it.d) ? it.d : undefined,
        });
      }
      if (attachments.length > 0) {
        return replyTo
          ? { kind: 'text_media', text: r.b, attachments, replyTo }
          : { kind: 'text_media', text: r.b, attachments };
      }
    }
    return { kind: 'text', text: decryptedUtf8 };
  } catch {
    return { kind: 'text', text: decryptedUtf8 };
  }
}
